import { genericMemo } from "@redotech/react-util/component";
import { ScreenSize } from "@redotech/redo-model/email-builder";
import { ReactNode } from "react";
import { RegisteredElements } from "../../email-builder/element-registrars/registered-elements";

export type WidgetProps<E> = { element: E };

/**
 * Decorator pattern: wraps FormElementProps to extend the functionality of a component
 * with editing-specific functionality like changing position, editing settings, etc.
 */
export interface WidgetEditorProps<T extends WidgetProps<E>, E> {
  widget: T;
  setElement: (element: E) => void;
  onSelectElement: (
    elementIndex: number | null,
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => void;
  selected: boolean;
  selectedColumnIndex?: number | null;
  setSelectedColumnIndex?: (index: number | null) => void;
  index: number;
  sectionToolbar?: React.ReactNode;
  /**
   * If true, the section toolbar will not be rendered,
   * but merely passed down to the child, who can render it however they want.
   */
  skipRenderingSectionToolbar?: boolean;
  screenSize?: ScreenSize;
  interactable?: boolean;
  renderFadeSeconds?: number;
  registeredElements?: RegisteredElements;
}

export type WidgetEditorDecorator<T extends WidgetProps<E>, E> = {
  children: (props: WidgetEditorProps<T, E>) => ReactNode;
} & WidgetEditorProps<T, E>;

export const withEditor = <T extends WidgetProps<E>, E>(
  Decorator: React.FC<WidgetEditorDecorator<T, E>>,
  Component: React.FC<T>,
) => {
  return genericMemo(function FormElementEditor(
    props: WidgetEditorProps<T, E>,
  ) {
    return (
      <Decorator {...props}>{() => <Component {...props.widget} />}</Decorator>
    );
  });
};
